import React, { useState, useEffect } from 'react'
import { useTheme } from '../hooks/useTheme'
import { HSLColor, calculateAccentColor, getDefaultColors } from '../utils/colorManagement'
import { hslToHex } from '../utils/colorConversion'
import { Undo, Palette, Save } from 'lucide-react'
import { useToast } from '../hooks/useToast'

interface ThemeColorPickerProps {
  onColorChange: (colors: { secondary: HSLColor; accent: HSLColor }) => void
  initialSecondary?: HSLColor
}

const ThemeColorPicker: React.FC<ThemeColorPickerProps> = ({
  onColorChange,
  initialSecondary
}) => {
  const { theme } = useTheme()
  const { showToast } = useToast()
  const isDarkMode = theme === 'dark'
  
  const [isEditing, setIsEditing] = useState(false)
  const [secondary, setSecondary] = useState<HSLColor>(
    initialSecondary || getDefaultColors(isDarkMode).secondary
  )
  const [tempSecondary, setTempSecondary] = useState<HSLColor>(secondary)
  const [hasChanges, setHasChanges] = useState(false)
  
  // Update colors when initialSecondary changes
  useEffect(() => {
    if (initialSecondary) {
      setSecondary(initialSecondary)
      setTempSecondary(initialSecondary)
    }
  }, [initialSecondary])
  
  useEffect(() => {
    // Only update the actual colors when not in editing mode
    if (!isEditing) {
      const accent = calculateAccentColor(secondary, isDarkMode)
      
      // Update CSS variables
      document.documentElement.style.setProperty(
        '--color-secondary',
        `hsl(${secondary.h}deg ${secondary.s}% ${secondary.l}%)`
      )
      document.documentElement.style.setProperty(
        '--color-accent',
        `hsl(${accent.h}deg ${accent.s}% ${accent.l}%)`
      )
      
      // Notify parent component
      onColorChange({ secondary, accent })
    }
  }, [secondary, isDarkMode, onColorChange, isEditing])
  
  const handleHueChange = (hue: number) => {
    const newSecondary = {
      ...tempSecondary,
      h: hue
    }
    setTempSecondary(newSecondary)
    setHasChanges(true)
    
    // Update preview
    const accent = calculateAccentColor(newSecondary, isDarkMode)
    document.documentElement.style.setProperty(
      '--color-secondary',
      `hsl(${newSecondary.h}deg ${newSecondary.s}% ${newSecondary.l}%)`
    )
    document.documentElement.style.setProperty(
      '--color-accent',
      `hsl(${accent.h}deg ${accent.s}% ${accent.l}%)`
    )
  }
  
  const handleSave = () => {
    setSecondary(tempSecondary)
    const accent = calculateAccentColor(tempSecondary, isDarkMode)
    onColorChange({ secondary: tempSecondary, accent })
    setHasChanges(false)
    setIsEditing(false)
    showToast('Theme colors saved', 'success')
  }
  
  const resetToDefault = () => {
    const defaults = getDefaultColors(isDarkMode)
    setSecondary(defaults.secondary)
    setTempSecondary(defaults.secondary)
    setIsEditing(false)
    setHasChanges(false)
    onColorChange({
      secondary: defaults.secondary,
      accent: calculateAccentColor(defaults.secondary, isDarkMode)
    })
    showToast('Theme colors reset to default', 'success')
  }
  
  const secondaryHex = hslToHex(isEditing ? tempSecondary : secondary)
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium">
          Current Color
        </label>
        <div className="flex items-center space-x-2">
          <button
            onClick={resetToDefault}
            className="btn btn-outline flex items-center py-1.5"
          >
            <Undo size={16} className="mr-2" />
            Reset to Default
          </button>
          {!isEditing ? (
            <button
              onClick={() => {
                setIsEditing(true)
                setTempSecondary(secondary)
              }}
              className="btn btn-primary flex items-center py-1.5"
            >
              <Palette size={16} className="mr-2" />
              Change Color
            </button>
          ) : (
            <button
              onClick={handleSave}
              className={`btn ${hasChanges ? 'btn-primary' : 'btn-outline'} flex items-center py-1.5`}
              disabled={!hasChanges}
            >
              <Save size={16} className="mr-2" />
              Save Changes
            </button>
          )}
        </div>
      </div>
      
      <div 
        className="h-16 rounded-lg border border-[var(--color-accent)] border-opacity-20"
        style={{ backgroundColor: secondaryHex }}
      />
      
      {isEditing && (
        <div className="mt-4">
          <input
            type="range"
            min="0"
            max="360"
            value={tempSecondary.h}
            onChange={(e) => handleHueChange(Number(e.target.value))}
            className="w-full h-3 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, 
                hsl(0, ${tempSecondary.s}%, ${tempSecondary.l}%),
                hsl(60, ${tempSecondary.s}%, ${tempSecondary.l}%),
                hsl(120, ${tempSecondary.s}%, ${tempSecondary.l}%),
                hsl(180, ${tempSecondary.s}%, ${tempSecondary.l}%),
                hsl(240, ${tempSecondary.s}%, ${tempSecondary.l}%),
                hsl(300, ${tempSecondary.s}%, ${tempSecondary.l}%),
                hsl(360, ${tempSecondary.s}%, ${tempSecondary.l}%)
              )`
            }}
          />
        </div>
      )}
    </div>
  )
}

export default ThemeColorPicker